/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useState } from "react";
import { MediaBatchPreparationInitServices } from "../../../../../../service/mediaBatchPrepInitServices";
import { MediaFineTunningPayload } from "../../../types/mediaFineTunning.types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface Props {
	onBack: () => void;
	onNext: () => void;
	fineTunningPayload: MediaFineTunningPayload;
	showToast: (message: string, toastType: "success" | "error") => void;
	preparationStatus?: string;
	validateFields: () => boolean;
}

const FineTuningFooter = ({
	onBack,
	onNext,
	fineTunningPayload,
	showToast,
	preparationStatus,
	validateFields,

}: Props) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const handleProceed = async () => {
		// Already completed Fine Tuning
		if (preparationStatus !== "Media Dispensed") {
			onNext();
			return;
		}
		// Validate only when creating Fine Tuning
		if (!validateFields()) {
			return;
		}
		setIsSubmitting(true);
		try {
			const response = await MediaBatchPreparationInitServices.postFineTunning(fineTunningPayload,);
			if (response?.data?.batch_status === "Rejected") {
			showToast(
				response?.message,
				"error"
			);
			setTimeout(() => {
				navigate("/mediaPreparationBatch");
			}, 2500);
			return;
		}
		showToast(
			response?.message ||
			"Fine Tuning Completed Successfully",
			"success",
		);

		setTimeout(() => {
			onNext();
		}, 2500);

		} catch (error: any) {
			const message =
				error?.response?.data?.message ||
				"An unexpected error occurred";
			showToast(message, "error");
		} finally {
			setIsSubmitting(false);
		}
	};
	return (
		<div className="bg-white border-t border-slate-200 px-10 py-2 flex items-center justify-between shrink-0 shadow-[0_-20px_40px_-20px_rgba(0,0,0,0.05)]">
			<button
				onClick={onBack}
				className="flex items-center gap-2 px-8 py-3.5 text-[11px] font-black text-slate-500 uppercase tracking-widest hover:text-red-500 transition-all border-none rounded-2xl"
			>
				<span className="material-symbols-outlined text-sm">
					arrow_back
				</span>
				{t("mediaBatchInit.fineTuning.backToDispensing")}
			</button>
			<button
				onClick={handleProceed}
				disabled={isSubmitting}
				className="bg-[#FF7043] text-white flex items-center text-[11px] gap-2 px-6 py-2.5 rounded-2xl shadow-xl shadow-orange-500/30 font-black uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all border-none disabled:opacity-50"
			>
				{isSubmitting ? t("mediaBatchInit.fineTuning.submitting") : t("mediaBatchInit.fineTuning.proceed")}
				<span className="material-symbols-outlined">arrow_forward</span>
			</button>
		</div>
	);
};

export default FineTuningFooter;
